import { APISchemas, GET, POST } from "@/api";
import {
  Button,
  Divider,
  Heading,
  Text,
  SimpleGrid,
  Box,
  Flex,
  Checkbox,
  useToast,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import { FC, useCallback, useEffect, useState } from "react";
import { CreditCard, PaymentSelect } from "./_payment-select";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQuery } from "react-query";
import { getCurrencySymbol } from "@/constant";
import { i18nformat } from "@/utils/misc";
import { useTranslation, Trans } from "react-i18next";
import { useOutsideStore } from "@/context/outside-store/outside-store";
import { useAuthStore } from "@/context/auth-store/auth-store";
import { addDays } from "date-fns";
import { PaymentSuccessIcon } from "@/assets/graphics/payment-success-icon";
import { TermsModal } from "../Privacy/components/terms-modal";

export const MembershipReview: FC = () => {
  const startPayment = useOutsideStore((c) => c.startPayment);
  const [selected, setSelected] = useState<CreditCard | undefined>();
  const [creditCard, setCreditCard] = useState<CreditCard>();
  const [loading, setLoading] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);
  const [successModal, openSuccessModal] = useState<boolean>(false);
  const [termsChecked, setTermsChecked] = useState<boolean>(false);
  const [formErrors, setFormErrors] = useState<
    {
      path?: string | undefined;
      message: string;
    }[]
  >([]);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { t } = useTranslation();

  const location = useLocation();
  const { newPlan } = location.state;

  const { data: Address } = useQuery(
    ["address", "review"],
    async () => {
      const { data, response } = await GET("/auth/address/", {});
      if (!response.ok) {
        toast({
          title: t("errors.please-enter-a-valid-address"),
          status: "error",
          isClosable: true,
        });

        navigate("/settings");
        return;
      }
      return data;
    },
    {
      cacheTime: 0,
      select(data) {
        if (data && data.length === 0) {
          toast({
            status: "error",
            title: t("errors.please-enter-a-valid-address"),
            isClosable: true,
          });
          navigate("/settings");
        }
        return data?.at(0);
      },
    }
  );

  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();

  if (!id) {
    navigate("/");
  }
  const [searchParams] = useSearchParams();
  const ok = searchParams.get("ok");

  const saveCard = useCallback(
    async (creditCard: CreditCard) => {
      if (!creditCard) {
        toast({
          status: "error",
          title: t("errors.please-enter-credit-card"),
          isClosable: true,
        });
        return;
      }

      const { error, data } = await POST("/payment/save-card/", {
        body: {
          cardHolderName: creditCard.name,
          cardNumber: creditCard.cardNumber,
          cardMonth: creditCard.expiryMonth,
          cardYear: "20" + creditCard.expiryYear,
        },
      });

      if (error?.issues) {
        setFormErrors(error.issues);
        error.issues.forEach((err) => {
          toast({
            description: err.message,
            status: "error",
            isClosable: true,
          });
        });

        return;
      }
      if (data?.result.startsWith("Invalid")) {
        toast({
          description: data.resultMessage,
          status: "error",
          isClosable: true,
        });

        return data;
      }

      return data;
    },
    [toast, t]
  );

  const finishPayment = useCallback(
    async (urlAddress: string) => {
      if (!creditCard?.CVV) return;
      const url = new URL(urlAddress);
      const searchParams = url.searchParams;
      const orderId = searchParams.get("OrderId");
      const systemTransId = searchParams.get("SystemTransId");
      const result = searchParams.get("Result");
      const totalAmount = searchParams.get("TotalAmount");
      const installmentCount = searchParams.get("InstallmentCount");
      const hash = searchParams.get("Hash");
      const mdStatus = searchParams.get("MDStatus") || "1";

      if (
        orderId &&
        systemTransId &&
        result &&
        totalAmount &&
        installmentCount &&
        hash &&
        mdStatus
      ) {
        const { response, error } = await POST("/payment/finish-payment/", {
          body: {
            orderId: orderId,
            systemTransId: systemTransId,
            result: result,
            totalAmount: Number(totalAmount),
            installmentCount: installmentCount,
            hash: hash,
            mdStatus: mdStatus,
            cvv: creditCard.CVV,
          },
        });
        if (response.ok) {
          openSuccessModal(true);
          return;
        }
        if (error?.detail) {
          toast({
            description: error.detail,
            status: "error",
            isClosable: true,
          });
          return;
        }
      } else {
        toast({
          status: "error",
          title: t("errors.unexpected-error-contact-customer-service"),
          isClosable: true,
        });
      }
    },
    [creditCard, toast, t]
  );

  const payAndConfirm = useCallback(async () => {
    if (!id) {
      toast({
        status: "error",
        title: t("errors.unexpected_error"),
        isClosable: true,
      });
      return;
    }

    if (!Address) {
      toast({
        status: "error",
        title: t("errors.please-enter-a-valid-address"),
        isClosable: true,
      });
      navigate("/settings");
      return;
    }

    if (!creditCard) {
      toast({
        status: "error",
        title: t("errors.please-select-a-credit-card"),
        isClosable: true,
      });
      return;
    }

    let cardToken: string | undefined;
    if (selected) {
      cardToken = selected.ctoken;
    } else {
      const saveCardData = await saveCard(creditCard);
      if (saveCardData?.cardToken) cardToken = saveCardData.cardToken;

      const { data, error } = await GET("/payment/card-list/", {});

      if (!data || error) {
        toast({
          description: t("errors.unexpected_error"),
          status: "error",
          isClosable: true,
        });
        return;
      }

      const savedCard = data.cardList?.find((x) => x.cardToken === cardToken);

      if (!savedCard) {
        toast({
          description: t("errors.unexpected_error"),
          status: "error",
          isClosable: true,
        });
        return;
      }

      creditCard.bankCode = savedCard.bankCode;
    }

    if (!cardToken) {
      toast({
        description: t("errors.unexpected_error"),
        status: "error",
        isClosable: true,
      });
      return;
    }
    const { data, error } = await POST("/payment/pay/", {
      body: {
        product_kind_id: Number(newPlan.plan_kind[0].id),
        product_id: Number(id),
        card_token: cardToken,
        cvv: creditCard.CVV,
        bank_code: creditCard.bankCode,
        address: Address.id.toString(),
      },
    });

    if (error?.issues) {
      setFormErrors(error.issues);
      error.issues.map((err) => {
        toast({
          description: err.message,
          status: "error",
          isClosable: true,
        });
      });

      return;
    }

    const newWindow = window.open();

    if (newWindow && data?.redirectForm) {
      if (newWindow && data?.redirectForm) {
        startPayment();
        newWindow.document.write(data?.redirectForm);
      }
    }
  }, [
    id,
    Address,
    creditCard,
    selected,
    toast,
    navigate,
    saveCard,
    t,
    startPayment,
  ]);

  useEffect(() => {
    if (ok === "true") {
      return;
    }

    setReady(true);
    setLoading(true);

    setLoading(false);
    const unSub = useOutsideStore.subscribe((c) => {
      if (!c.state.url) return;
      finishPayment(c.state.url);
      c.clearState();
    });
    return () => {
      unSub();
    };
  }, [ok, finishPayment]);

  return (
    <Box p="7" bg="white">
      <SimpleGrid columns={2} w="full">
        <Box
          boxShadow="0px 0px 94px 13px rgba(191, 191, 191, 0.25)"
          h="full"
          py="6"
          px="5"
          bg="white"
        >
          <Heading size="lg" pb="5">
            {t("page.membership.review.payment-checkout")}
          </Heading>
          <Box px="6vw">
            <Text fontSize="24px" fontWeight="600">
              {t("page.membership.review.payment")}
            </Text>
            <Divider borderColor="gray.passive" my="8px" mb="27px" />
            <Box mb="5">
              <Text fontSize="xl" fontWeight="medium" mb="4">
                {t("page.membership.Choose-a-payment-method")}
              </Text>
              <PaymentSelect
                formError={formErrors}
                selected={selected}
                hardValidation={termsChecked}
                setSelected={(item) => setSelected(item)}
                onChange={setCreditCard}
                onValid={(valid) => setReady(valid)}
              />
              <Divider borderColor="gray.passive" my="8px" mb="27px" />
              <Box>
                <Text fontSize="xl" fontWeight="medium" mb="4" display="inline">
                  {t("billing_address")}
                </Text>{" "}
                :
                <Text
                  ml="3"
                  as="a"
                  href="/settings"
                  color="gray.passive"
                  textDecor="underline"
                >
                  {Address?.address_type === "B"
                    ? t("company")
                    : t("individual")}{" "}
                  - {Address?.name} - {Address?.city}
                </Text>
              </Box>
            </Box>
            <Box px="35px">
              <Checkbox
                color="black.dark"
                fontSize="16px"
                mb="48px"
                onChange={() => {
                  setTermsChecked(!termsChecked);
                }}
                isChecked={termsChecked}
              >
                {t("page.membership.I-read-and-accepted")}
                <Button
                  variant="link"
                  textDecor="underline"
                  fontWeight="700"
                  color="blue.shiny"
                  display="inline-block"
                  _hover={{
                    borderBottom: "none",
                  }}
                  ml="1"
                  mr="1"
                  onClick={onOpen}
                >
                  {t("page.membership.payment-terms")} &
                  {t("page.membership.policy-conditions")}
                </Button>
              </Checkbox>
              <Text color="gray.passive" fontSize="14px" mt="11px">
                {t("page.membership.review.personal-data-message")}
              </Text>

              <Button
                mt="23px"
                bg="#50CD89"
                color="white"
                w="full"
                rounded="md"
                py="18px"
                size="none"
                boxShadow="xl"
                _hover={{
                  opacity: "0.8",
                }}
                type="submit"
                isDisabled={loading || !ready || !termsChecked}
                onClick={() => payAndConfirm()}
              >
                {t("page.membership.pay-and-confirm")}
              </Button>
            </Box>
          </Box>
        </Box>

        <Flex
          flexDir="column"
          pt="3.5"
          px="6"
          pb="14"
          color="black"
          overflow="hidden"
          bg="white.snow-white"
          alignItems="center"
        >
          <Box w="476px">
            <Text fontSize="24px" fontWeight="600">
              {t("page.membership.review.order-summary")}
            </Text>
            <Divider borderColor="gray.passive" my="8px" mb="27px" />
            <CreditCardPreview card={creditCard} />
            <Box w="full" mt="7" lineHeight="1" pl="2" pr="8">
              <Flex justifyContent="space-between">
                <Text
                  fontSize="xl"
                  fontWeight="semibold"
                  mb="5"
                  textTransform="capitalize"
                >
                  {newPlan?.name &&
                    t(`membership-packages.${newPlan.name.toLowerCase()}`)}
                </Text>
                <Text fontSize="20px" fontWeight="500">
                  {newPlan.plan_kind[0].price} {newPlan.plan_kind[0].currency}
                </Text>
              </Flex>
              <Text mb="5" fontSize="16px">
                {newPlan?.description &&
                  t(`membership-packages.${newPlan.description.toLowerCase()}`)}
              </Text>

              <Flex justifyContent="space-between" color="gray.passive">
                <Text fontSize="16px" fontWeight="500">
                  {t("page.membership.review.valid-until")}
                </Text>
                <Text fontSize="16px" fontWeight="500">
                  {i18nformat(
                    addDays(
                      Date.now(),
                      newPlan.plan_kind[0].plan_period === "monthly"
                        ? 30
                        : 365 ?? 0
                    ),
                    "dd MMMM yyyy"
                  )}
                </Text>
              </Flex>
              <Divider borderColor="gray.passive" my="9" />
              <Flex
                fontSize="16px"
                justifyContent="space-between"
                fontWeight="600"
                mb="4"
              >
                <Text>{t("page.membership.review.sub-total")}</Text>
                <Text>
                  {getCurrencySymbol(newPlan.plan_kind[0].currency ?? "usd")}
                  {newPlan.plan_kind[0].price} {newPlan.plan_kind[0].currency}
                </Text>
              </Flex>
              {/* <Flex
                fontSize="16px"
                justifyContent="space-between"
                fontWeight="600"
              >
                <Text>{t("page.membership.Taxes")}</Text>
                <Text>
                  {getCurrencySymbol(Plan?.currency ?? "usd")}0.00{" "}
                  {Plan?.currency}
                </Text>
              </Flex> */}
              <Divider mb="5" mt="8" borderColor="gray.passive" />
              <Flex justifyContent="space-between" fontWeight="medium" mb="8">
                <Text fontSize="20px" fontWeight="500">
                  {t("page.membership.Total")}:
                </Text>
                <Text fontSize="36px">
                  {getCurrencySymbol(newPlan.plan_kind[0].currency ?? "usd")}
                  {newPlan.plan_kind[0].price} {newPlan.plan_kind[0].currency}{" "}
                </Text>
              </Flex>
            </Box>
          </Box>
        </Flex>
      </SimpleGrid>
      {newPlan && <SuccessModal isOpen={successModal} plan={newPlan} />}
      <TermsModal isOpen={isOpen} onClose={onClose} />
    </Box>
  );
};

const SuccessModal: FC<{ isOpen: boolean; plan: APISchemas["Plan"] }> = ({
  isOpen,
  plan,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const onClose = useCallback(() => null, []);

  const date = i18nformat(
    addDays(
      Date.now(),
      plan.plan_kind[0].plan_period === "monthly" ? 30 : 365 ?? 0
    ),
    "dd MMMM yyyy"
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl">
      <ModalOverlay />
      <ModalContent py="8">
        <ModalBody
          textAlign="center"
          bg="white"
          rounded="6px"
          py="55px"
          px="18px"
        >
          <SimpleGrid columns={2}>
            <PaymentSuccessIcon />

            <Box>
              <Heading
                fontSize="36px"
                fontWeight="700"
                color="black.dark"
                lineHeight="1"
              >
                {t("page.membership.success-modal.well-done")}
              </Heading>

              <Text
                fontSize="20px"
                color="black.dark"
                mt="24px"
                mb="56px"
                textAlign="left"
                lineHeight="41px"
              >
                {t("page.membership.success-modal.message")}{" "}
                <Text
                  as="span"
                  color="#57AAEC"
                  fontWeight="600"
                  textTransform="capitalize"
                >
                  {t(`membership-packages.${plan.name.toLowerCase()}`)}
                </Text>{" "}
                <Trans
                  i18nKey="page.membership.success-modal.until"
                  values={{ date }}
                >
                  <span style={{ color: "#57AAEC", fontWeight: 600 }}>
                    {date}
                  </span>
                </Trans>
              </Text>

              <Button
                bg="#50CD89"
                color="white"
                fontSize="24px"
                p="24px"
                w="full"
                onClick={() => navigate("/")}
              >
                {t("page.membership.success-modal.back-to-dashboard")}
              </Button>
            </Box>
          </SimpleGrid>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const CreditCardPreview: FC<{ card?: CreditCard }> = ({ card }) => {
  const { t } = useTranslation();
  return (
    <Box
      w="476px"
      h="241px"
      border="1.758px solid #B7BABF"
      boxShadow="14.647px 17.576px 29.294px 0px rgba(0, 0, 0, 0.05)"
      backdropFilter="blur(10.252809524536133px)"
      bg="linear-gradient(113deg, rgba(255, 255, 255, 0.51) 3.51%, rgba(255, 255, 255, 0.00) 111.71%)"
      rounded="3xl"
      color="black.dark"
      fontWeight="600"
    >
      <Flex
        justifyContent="space-between"
        mt="29px"
        ml="27px"
        mr="25px"
        alignItems="center"
      >
        <ChipIcon />
        <WirelessIcon />
      </Flex>
      <Box ml="23px" mt="21px" mb="62px">
        <Text
          fontSize="xs"
          letterSpacing="wide"
          opacity="0.9"
          mb="8px"
          lineHeight="8px"
        >
          {t("page.membership.Card_Number")}
        </Text>
        <Text
          fontSize="15px"
          letterSpacing="wide"
          opacity="0.9"
          lineHeight="14px"
        >
          {card?.cardNumber
            ?.replace(/([0-9*]{4})/g, (a: string) => a + " ")
            .trim()}
        </Text>
      </Box>
      <Flex ml="23px" alignItems="center" mb="32px">
        <Text
          fontSize="15px"
          letterSpacing="wide"
          opacity="0.9"
          lineHeight="14px"
          mr="108px"
          w="20ch"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {card?.name}
        </Text>
        <Box>
          <Text
            fontSize="xs"
            letterSpacing="wide"
            opacity="0.9"
            lineHeight="8px"
            mb="9px"
          >
            {t("page.membership.valid_thru")}
          </Text>
          <Text
            fontSize="15px"
            letterSpacing="wide"
            opacity="0.9"
            lineHeight="14px"
          >
            {card?.expiryMonth || "**"}/{card?.expiryYear || "**"}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

const ChipIcon: FC = () => {
  return (
    <svg
      width="52"
      height="45"
      viewBox="0 0 52 45"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H52V45H0V0Z" fill="url(#chip-pattern)" />
      <defs>
        <pattern
          id="chip-pattern"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use
            xlinkHref="#chip-icon"
            transform="matrix(0.00195312 0 0 0.00225694 0 -0.0777778)"
          />
        </pattern>
        <image
          id="chip-icon"
          width="512"
          height="512"
          xlinkHref="data:image/png;base64,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"
        />
      </defs>
    </svg>
  );
};

const WirelessIcon: FC = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="25"
      height="29"
      viewBox="0 0 25 29"
      fill="none"
    >
      <path
        d="M13.1682 4.44284C12.4469 3.85239 11.2729 3.85239 10.5445 4.44284C9.82321 5.03328 9.82321 5.99421 10.5445 6.59045C15.5372 10.6773 15.5372 17.3285 10.5445 21.4153C10.1839 21.7106 10 22.0984 10 22.4863C10 22.8741 10.1839 23.2619 10.5445 23.5572C11.2659 24.1476 12.4398 24.1476 13.1682 23.5572C19.6106 18.2894 19.6106 9.71056 13.1682 4.44284Z"
        fill="#353B48"
      />
      <path
        d="M8.20812 8.45044C7.4777 7.84985 6.28897 7.84985 5.55139 8.45044C4.82097 9.05103 4.82097 10.0285 5.55139 10.6349C7.81426 12.4897 7.81426 15.5162 5.55139 17.371C5.18618 17.6712 5 18.0658 5 18.4603C5 18.8548 5.18618 19.2493 5.55139 19.5496C6.28181 20.1501 7.47053 20.1501 8.20095 19.5496C11.9318 16.4936 11.9318 11.5123 8.20812 8.45044Z"
        fill="#353B48"
      />
      <path
        d="M3.74644e-07 14C3.18204e-07 15.1046 0.895432 16 2 16C3.10457 16 4 15.1046 4 14C4 12.8954 3.10457 12 2 12C0.895432 12 4.31084e-07 12.8954 3.74644e-07 14Z"
        fill="#353B48"
      />
      <path
        d="M18.1384 28.5466C27.2872 20.8002 27.2872 8.19978 18.1384 0.453403C17.4238 -0.151135 16.2609 -0.151135 15.5394 0.453402C14.8249 1.05794 14.8249 2.04179 15.5394 2.65226C23.2662 9.18363 23.2662 19.8164 15.5394 26.3537C15.1821 26.6559 15 27.053 15 27.4501C15 27.8472 15.1821 28.2443 15.5394 28.5466C16.2539 29.1511 17.4168 29.1511 18.1384 28.5466Z"
        fill="#353B48"
      />
    </svg>
  );
};
